import type { NotificationsListResponse } from '@paladin-monorepo/common';
import { useState } from 'react';
import { toast } from 'sonner';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { notificationsApi } from '@/lib/api/notifications';
import { SidebarNotificationIcon } from '@/components/sidebar/sidebar-icons';
import { cn } from '@/lib/utils';

import { notificationTypeWeight, weightToColorMap } from './colors';
import { NotificationItem } from './NotificationItem';

type NotificationsProps = {
  variant?: 'default' | 'sidebar';
  className?: string;
};

export const Notifications = ({
  variant = 'default',
  className,
}: NotificationsProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [includeRead, setIncludeRead] = useState(false);

  const queryKey = ['notifications', { includeRead }] as const;

  const {
    data: notifications,
    error,
    isLoading,
  } = useQuery({
    queryKey,
    queryFn: () => notificationsApi.list(includeRead),
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<NotificationsListResponse>(queryKey);
      if (previous) {
        const optimistic: NotificationsListResponse = {
          data: previous.data.map((notification) => ({
            ...notification,
            readAt: new Date().toISOString(),
          })),
          total: previous.total,
        };
        queryClient.setQueryData(queryKey, optimistic);
      }
      return { previous };
    },
    onError: (err, _vars, context) => {
      console.error(err);
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
      toast.error(t('notifications.markAllReadError'));
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markReadMutation = useMutation({
    mutationFn: (notificationId: string) => notificationsApi.markRead(notificationId),
    onMutate: async (notificationId) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<NotificationsListResponse>(queryKey);
      if (previous) {
        const optimistic: NotificationsListResponse = {
          data: previous.data.map((n) =>
            n.id === notificationId ? { ...n, readAt: new Date().toISOString() } : n,
          ),
          total: previous.total,
        };
        queryClient.setQueryData(queryKey, optimistic);
      }
      return { previous };
    },
    onError: (err, _notificationId, context) => {
      console.error(err);
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
      toast.error(t('notifications.markReadError'));
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const hasUnreadNotifications: number | false =
    notifications?.data.reduce(
      (acc, notification): number | false =>
        notification.readAt === null
          ? Math.max(notificationTypeWeight[notification.type], acc || 0)
          : acc,
      false,
    ) ?? false;

  const isSidebar = variant === 'sidebar';

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t('notifications.ariaLabel')}
          className={cn(
            'relative transition-colors',
            isSidebar
              ? 'flex size-[35px] shrink-0 items-center justify-center rounded-md border border-white/50 text-white hover:bg-white/10'
              : 'rounded-full p-2 hover:bg-muted',
            className,
          )}
        >
          <SidebarNotificationIcon className={isSidebar ? 'size-3.5' : 'size-5'} />
          {hasUnreadNotifications && (
            <span
              className={cn(
                'absolute rounded-full',
                isSidebar ? 'top-1 right-1 size-[7px]' : 'top-1 right-1 h-2 w-2',
                weightToColorMap[hasUnreadNotifications],
              )}
            />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0">
        <div className="p-2 flex flex-col gap-1 overflow-y-scroll max-h-[80vh]">
          {hasUnreadNotifications && (
            <div
              className="relative cursor-pointer rounded-md p-2 transition-colors hover:bg-muted"
              onClick={() => markAllReadMutation.mutate()}
            >
              <div className="text-sm font-medium text-right text-muted-foreground">
                {t('notifications.markAllRead')}
              </div>
            </div>
          )}
          {!isLoading &&
            !error &&
            notifications &&
            notifications.data.length === 0 && (
              <div className="">
                <p className="text-sm font-medium text-center text-muted-foreground">
                  {t('notifications.empty')}
                </p>

                {!includeRead && (
                  <p
                    className="pt-2 text-sm text-center cursor-pointer underline"
                    onClick={() => setIncludeRead(true)}
                  >
                    {t('notifications.showAll')}
                  </p>
                )}
              </div>
            )}
          {!isLoading &&
            !error &&
            notifications &&
            notifications?.data.map((notification) => {
              return (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onRead={(notification) => {
                    markReadMutation.mutate(notification.id);
                  }}
                />
              );
            })}
        </div>
      </PopoverContent>
    </Popover>
  );
};
